import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { GatsbyImage, getImage } from 'gatsby-plugin-image';
import { useStaticQuery, graphql } from 'gatsby';
import { Link } from 'gatsby';
import { Seo } from '../components/SEO';

const PageContainer = styled.div`
  width: 100%;
  height: 100vh;
  display: flex;
  flex-direction: column;
  overflow: hidden;

  @media (min-width: 1024px) {
    flex-direction: row;
  }
`;

const Row = styled.div`
  display: flex;
  flex-direction: row;
  flex: 1;
  border-bottom: 1px solid #333;
  position: relative;
  &:last-child {
    border-bottom: none;
  }
  @media (min-width: 1024px) {
    flex-direction: column;
    border-right: 1px solid #333;
    &:last-child {
      border-right: none;
    }
  }
`;

const ProjectContainer = styled(motion.div)`
  flex: 1;
  position: relative;
  overflow: hidden;
  display: flex;
  justify-content: center;
  align-items: center;
  height: calc(100vh / 4);

  @media (min-width: 1024px) {
    height: auto;
  }
`;

const BackgroundImageWrapper = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
`;

const VideoBackground = styled.video`
  width: 100%;
  height: 100%;
  object-fit: cover;
  position: absolute;
  top: 0;
  left: 0;
`;

const Window = styled(motion.div)`
  position: absolute;
  left: 0;
  right: 0;
  height: 50%;
  background-color: #f3f3f3;
  z-index: 5;
`;

const TopWindow = styled(Window)`
  top: 0;
`;

const BottomWindow = styled(Window)`
  bottom: 0;
`;

const CategoryLabel = styled.div`
  display: none;

  @media (min-width: 1024px) {
    display: flex;
    height: 80px;
    align-items: center;
    justify-content: center;
    color: black;
    font-size: 18px;
    background-color: #f3f3f3;
    text-align: center;
    padding: 0 10px;
  }
`;

const MobileCategoryLabel = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  color: black;
  font-size: 18px;
  padding: 10px 0px;
  border-radius: 4px;
  z-index: 10;
  pointer-events: none;

  @media (min-width: 1024px) {
    display: none;
  }
`;

const IconContainer = styled(motion.div)`
  position: absolute;
  width: 100px;
  height: 100px;
  z-index: 6;
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: pointer;

  @media (max-width: 1023px) {
    width: 80px;
    height: 80px;
  }
`;

const IconLink = styled(Link)`
  display: none;
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;

  @media (max-width: 1023px) {
    display: flex;
  }
`;

const IconImage = styled.img`
  max-width: 60%;
  max-height: 60%;
  transition: transform 0.3s ease;

  @media (min-width: 1024px) {
    ${ProjectContainer}:hover & {
      transform: scale(1.1);
    }
  }

  @media (max-width: 1023px) {
    max-width: 70%;
    max-height: 70%;
    &:active {
      transform: scale(0.9);
    }
  }
`;

const BackgroundLink = styled(Link)`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 1;

  @media (max-width: 1023px) {
    display: none;
  }
`;

const TextBackground = styled(motion.div)`
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.7);
  padding: 20px;
  transform: translateY(100%);
  z-index: 2;
`;

const ProjectText = styled.div`
  color: white;
  font-size: 16px;
  text-align: center;
`;

const Project = ({ backgroundImage, icon, text, link }) => {
  const bgImage = getImage(backgroundImage);
  const isVideo = backgroundImage?.publicURL?.endsWith('.mp4');

  return (
    <ProjectContainer whileHover="hover" initial="initial">
      <BackgroundLink to={link}>
        <BackgroundImageWrapper>
          {isVideo ? (
            <VideoBackground
              autoPlay
              loop
              muted
              playsInline
              src={backgroundImage.publicURL}
            />
          ) : (
            <GatsbyImage
              image={bgImage}
              alt="Project Background"
              style={{ height: '100%' }}
            />
          )}
        </BackgroundImageWrapper>
      </BackgroundLink>
      <TopWindow
        variants={{
          initial: { y: 0 },
          hover: { y: '-100%' },
        }}
        transition={{ duration: 0.25, ease: 'easeInOut' }}
      />
      <IconContainer
        variants={{
          initial: { opacity: 1, scale: 1 },
          hover: { opacity: 0, scale: 0.8 },
        }}
        transition={{ duration: 0.3, ease: 'easeInOut' }}
      >
        <IconLink to={link}>
          <IconImage src={icon} alt="Project Icon" />
        </IconLink>
      </IconContainer>
      <TextBackground
        variants={{
          initial: { y: '100%' },
          hover: { y: 0 },
        }}
        transition={{ duration: 0.25, ease: 'easeInOut' }}
      >
        <ProjectText>{text}</ProjectText>
      </TextBackground>
      <BottomWindow
        variants={{
          initial: { y: 0 },
          hover: { y: '100%' },
        }}
        transition={{ duration: 0.3, ease: 'easeInOut' }}
      />
    </ProjectContainer>
  );
};

const Design = () => {
  const data = useStaticQuery(graphql`
    query {
      images: allFile(
        filter: {
          relativePath: { regex: "/DesignProjects/" }
          extension: { in: ["jpg", "jpeg", "png", "webp", "gif"] }
        }
      ) {
        edges {
          node {
            relativePath
            publicURL
            extension
            childImageSharp {
              gatsbyImageData(layout: FULL_WIDTH)
            }
          }
        }
      }
      svgs: allFile(
        filter: {
          relativePath: { regex: "/DesignProjects/" }
          extension: { eq: "svg" }
        }
      ) {
        edges {
          node {
            relativePath
            publicURL
            extension
          }
        }
      }
      videos: allFile(
        filter: {
          relativePath: { regex: "/DesignProjects/" }
          extension: { in: ["mp4", "webm"] }
        }
      ) {
        edges {
          node {
            relativePath
            publicURL
            extension
          }
        }
      }
    }
  `);

  const getFileByPath = (path) => {
    const adjustedPath = path.replace('.gif', '.mp4').replace('../images/', '');
    // Search images first
    let file = data.images.edges.find(
      (edge) => edge.node.relativePath === adjustedPath
    );
    // If not found, search videos
    if (!file) {
      file = data.videos.edges.find(
        (edge) => edge.node.relativePath === adjustedPath
      );
    }
    // If not found, search svgs
    if (!file) {
      file = data.svgs.edges.find(
        (edge) => edge.node.relativePath === adjustedPath
      );
    }
    return file ? file.node : null;
  };

  const getIconPath = (path) => {
    const relativePath = path.replace('../images/', '');
    // Search images first
    let icon = data.images.edges.find(
      (edge) => edge.node.relativePath === relativePath
    );
    // If not found, search svgs
    if (!icon) {
      icon = data.svgs.edges.find(
        (edge) => edge.node.relativePath === relativePath
      );
    }
    return icon ? icon.node.publicURL : null;
  };

  const categories = [
    {
      label: 'WebXR',
      projects: [
        {
          backgroundImage: getFileByPath(
            '../images/DesignProjects/Block/cover.mp4'
          ),
          icon: getIconPath('../images/DesignProjects/Block/icon.png'),
          text: 'WebXR Block Puzzle Game Design',
          link: '/projects/ZLT',
        },
        {
          backgroundImage: getFileByPath(
            '../images/DesignProjects/Devil/cover.mp4'
          ),
          icon: getIconPath('../images/DesignProjects/Devil/icon.png'),
          text: 'Facial Expression Filter Game',
          link: '/projects/DEVIL',
        },
      ],
    },
    {
      label: 'UX/Design Reserach',
      projects: [
        {
          backgroundImage: getFileByPath(
            '../images/DesignProjects/CAR/cover.mp4'
          ),
          icon: getIconPath('../images/DesignProjects/CAR/icon.png'),
          text: 'On-campus autonomous shuttle experience',
          link: '/projects/SHUTTLE',
        },
        {
          backgroundImage: getFileByPath(
            '../images/DesignProjects/ORION/cover.png'
          ),
          icon: getIconPath('../images/DesignProjects/ORION/icon.png'),
          text: 'Design the future work system',
          link: '/projects/ORION',
        },
      ],
    },
    {
      label: 'MR Concept',
      projects: [
        {
          backgroundImage: getFileByPath(
            '../images/DesignProjects/Orgo/cover.mp4'
          ),
          icon: getIconPath('../images/DesignProjects/Orgo/icon.png'),
          text: 'Hybrid socialization in future work station',
          link: '/projects/ORGO',
        },
        {
          backgroundImage: getFileByPath(
            '../images/DesignProjects/mixxy/cover.mp4'
          ),
          icon: getIconPath('../images/DesignProjects/mixxy/icon.png'),
          text: 'Cyber drinking experience with hands gesture',
          link: '/projects/Mixxy',
        },
      ],
    },
    {
      label: 'Data Representation',
      projects: [
        {
          backgroundImage: getFileByPath(
            '../images/DesignProjects/Covid/cover.mp4'
          ),
          icon: getIconPath('../images/DesignProjects/Covid/icon.png'),
          text: 'Data visualization of total COVID death in the U.S.',
          link: '/projects/COVID',
        },
        {
          backgroundImage: getFileByPath(
            '../images/DesignProjects/Toio/cover.mp4'
          ),
          icon: getIconPath('../images/DesignProjects/Toio/icon.png'),
          text: 'Physicalizing Speed data',
          link: '/projects/Traffic-viz',
        },
      ],
    },
  ];

  return (
    <PageContainer>
      {categories.map((category, index) => (
        <Row key={index}>
          <Project {...category.projects[0]} />
          <CategoryLabel>{category.label}</CategoryLabel>
          <MobileCategoryLabel>{category.label}</MobileCategoryLabel>
          <Project {...category.projects[1]} />
        </Row>
      ))}
    </PageContainer>
  );
};

export default Design;

export const Head = () => (
  <Seo
    title="Design Projects - Jesse Gao"
    description="Explore Jesse's design projects including WebXR experiences, UX research, mixed reality concepts, and data visualization work."
    pathname="/design/"
  />
);
